// «Despliegues pendientes de tu Allow» (Pau, 28-09; sprint 127 F4): la primera sección del panel del
// equipo, porque es lo que más urge (algo espera para ir a producción). Una fila por despliegue; al
// pulsarla se abre su ventana con el detalle y el Allow. Si no hay nada pendiente, no ocupa sitio.
import { ChevronRight, KeyRound, Rocket } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { hace } from '../marco/estado';
import type { Despliegue, ItemSube, Linea } from './api';
import { useBandejaStore, useBandejaViva } from './store';
import { FijarContrasena, VentanaDespliegue } from './VentanaDespliegue';
import { Ventana } from '../ui';

/** Abierta o plegada, recordado en este navegador (por defecto, plegada). */
const CLAVE_ABIERTAS = 'oficina:subidas-abiertas';

function leerAbiertas(): boolean {
  try {
    return localStorage.getItem(CLAVE_ABIERTAS) === '1';
  } catch {
    // Sin almacenamiento (modo privado estricto): plegada, como la primera vez.
    return false;
  }
}

export function BandejaDespliegues() {
  useBandejaViva();
  const [abiertas, setAbiertas] = useState(leerAbiertas);
  const idLista = useId();
  const alternar = () => {
    const nuevo = !abiertas;
    setAbiertas(nuevo);
    try {
      localStorage.setItem(CLAVE_ABIERTAS, nuevo ? '1' : '0');
    } catch {
      // Sin almacenamiento: se abre igual, solo que no se recuerda.
    }
  };
  const bandeja = useBandejaStore((s) => s.bandeja);
  // 29-09: se guarda solo QUÉ despliegue está abierto; el despliegue en sí sale siempre de la bandeja viva.
  // Antes se guardaba el objeto de cuando se pulsó: si en GitHub cambiaba (otro intento, otra versión) la
  // ventana seguía enseñando y aprobando lo viejo, y si ya no estaba pendiente seguía abierta.
  const [abiertoId, setAbiertoId] = useState<number | null>(null);
  const pendientes = bandeja?.pendientes ?? [];
  const abierto = despliegueAbierto(pendientes, abiertoId);
  useEffect(() => {
    if (abiertoId !== null && !abierto) setAbiertoId(null);
  }, [abiertoId, abierto]);
  const incompleta = bandeja?.incompleta ?? false;
  // Sin contraseña no se puede aprobar ni devolver nada: la fila para fijarla está SIEMPRE (Pau 28-09: «¿cómo
  // pongo mi contraseña?» — solo se podía desde un despliegue pendiente, y sin ninguno no había por dónde).
  const sinContrasena = !!bandeja && !bandeja.contrasenaFijada;
  if (!pendientes.length && !incompleta) return sinContrasena ? <FilaContrasena /> : null;
  const escriben = pendientes.filter((d) => !d.soloLectura).length;

  const conAviso = pendientes.filter((d) => d.avisos.length).length;
  const urge = escriben > 0;

  return (
    <section className="mb-1.5">
      {/* 10-10 (Pau aprobó «las subidas en una línea»): plegada por defecto, una sola línea con cuántas esperan y
          cuántas traen aviso. Abiertas, las 3 tarjetas grandes empujaban a la gente media pantalla abajo (en el
          móvil, la primera persona salía por debajo del pliegue). Lo que Pau elija (abierta o plegada) se recuerda
          en este navegador: si las quiere ver siempre, las deja abiertas. El aviso de lectura incompleta sigue
          siempre a la vista: es un fallo, no detalle. */}
      <button
        type="button"
        onClick={alternar}
        aria-expanded={abiertas}
        aria-controls={idLista}
        className={cn(
          'flex min-h-11 w-full items-center gap-2 rounded-o-m px-2 text-left text-o-sub font-semibold leading-5 outline-none transition-colors hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
          urge ? 'text-oficina-bloqueado' : 'text-oficina-texto2',
        )}
        data-testid="subidas-linea"
      >
        <Rocket className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
        <span className="min-w-0 flex-1">
          {t(pendientes.length === 1 ? 'oficinaV2.despliegues.tituloUno' : 'oficinaV2.despliegues.tituloVarios', { n: pendientes.length })}
          {conAviso ? <span className="text-oficina-error">{' · '}{t('oficinaV2.despliegues.conAviso', { n: conAviso })}</span> : null}
        </span>
        <ChevronRight className={cn('h-4 w-4 shrink-0 text-oficina-texto3 transition-transform motion-reduce:transition-none', abiertas && 'rotate-90')} aria-hidden />
      </button>
      {incompleta ? <p className="px-2 pb-1 text-o-nota text-oficina-error">{t('oficinaV2.despliegues.incompleta')}</p> : null}
      {abiertas ? (
        <ul id={idLista}>
          {pendientes.map((d) => (
            <li key={d.runId} className="mb-1">
              <button
                type="button"
                onClick={() => setAbiertoId(d.runId)}
                className={cn(
                  // 03-11 (Apple): sin barra lateral; el color lo lleva el relleno y el icono.
                  'flex w-full items-center gap-3 rounded-o-m py-2.5 pl-2.5 pr-2 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
                  d.soloLectura ? 'bg-white/[0.04] hover:bg-white/[0.07]' : 'bg-oficina-bloqueado/[0.10] hover:bg-oficina-bloqueado/[0.16]',
                )}
              >
                <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', d.soloLectura ? 'bg-white/[0.08] text-oficina-texto2' : 'bg-oficina-bloqueado/20 text-oficina-bloqueado')}>
                  <Rocket className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  {/* 10-10: el título usa todo el ancho (hasta 2 líneas); la hora baja delante de la frase, como en
                      las filas de la gente (al lado del título lo cortaba a ~15 letras en 304 px). */}
                  <span className="block">
                    <span className="line-clamp-2 break-words text-o-cuerpo font-semibold leading-5 text-oficina-texto">{d.paraPau.titulo}</span>
                  </span>
                  {/* Lo primero que tiene que saber: un aviso en rojo si lo hay; si no, qué cambia en sus palabras. */}
                  <span className={cn('mt-0.5 line-clamp-2 text-o-sub leading-[18px]', d.avisos.length ? 'font-semibold text-oficina-error' : 'text-oficina-texto2')}>
                    <span className="font-normal text-oficina-texto3">
                      <span className="text-o-nota tabular-nums" data-testid="subida-hora">{hace(d.creadoEn)}</span>
                      {' · '}
                    </span>
                    {d.avisos.length
                      ? d.avisos[0]
                      : d.esDeploy && d.lineas.estado === 'listo'
                        ? resumenLineas(d.lineas.lineas)
                      : d.loQueSube.estado === 'ok'
                        ? resumenFila(d.loQueSube.items)
                        : d.paraPau.cambiaEstado === 'listo' ? d.paraPau.cambia : d.paraPau.cambiaEstado === 'preparando' ? t('oficinaV2.despliegues.preparando') : d.paraPau.apruebas}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {abiertas && escriben === 0 && pendientes.length > 3 ? <p className="px-2 text-o-nota text-oficina-texto3">{t('oficinaV2.despliegues.muchosDiagnosticos')}</p> : null}
      <VentanaDespliegue despliegue={abierto} alCerrar={() => setAbiertoId(null)} />
    </section>
  );
}

/** 05-10: en la fila, de quién es lo que toca a los fans (lo primero que Pau quiere recordar). */
export function resumenFila(items: ItemSube[]): string {
  const fans = items.filter((i) => i.afectaFans);
  if (!fans.length) return t('oficinaV2.despliegues.filaNingunoFans');
  const quienes = [...new Set(fans.map((i) => i.agente ?? t('oficinaV2.desconocido')))].join(', ');
  return t(fans.length === 1 ? 'oficinaV2.despliegues.filaFansUno' : 'oficinaV2.despliegues.filaFansVarios', { n: fans.length, quienes });
}

/** 13-10: con las líneas listas, cuántas cosas cambian y cuántas no hablaste con su agente (lo que hay que mirar). */
export function resumenLineas(lineas: Linea[]): string {
  const cambian = t(lineas.length === 1 ? 'oficinaV2.despliegues.lineas.cambiaUna' : 'oficinaV2.despliegues.lineas.cambiaVarias', { n: lineas.length });
  const sinHablar = lineas.filter((l) => l.hablado === 'no' || l.hablado === 'rechazado').length;
  return sinHablar ? `${cambian} · ${t(sinHablar === 1 ? 'oficinaV2.despliegues.lineas.filaSinHablarUna' : 'oficinaV2.despliegues.lineas.filaSinHablarVarias', { n: sinHablar })}` : cambian;
}

/** El despliegue abierto tal como está AHORA en la bandeja; null si ya no está pendiente. */
export function despliegueAbierto(pendientes: Despliegue[], runId: number | null): Despliegue | null {
  return runId === null ? null : pendientes.find((d) => d.runId === runId) ?? null;
}

function FilaContrasena() {
  const [abierta, setAbierta] = useState(false);
  return (
    <section className="mb-1.5 pt-2">
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="flex w-full items-center gap-3 rounded-o-m bg-oficina-bloqueado/[0.10] py-2.5 pl-3 pr-2 text-left outline-none transition-colors hover:bg-oficina-bloqueado/[0.16] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-oficina-bloqueado/20 text-oficina-bloqueado">
          <KeyRound className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.despliegues.filaContrasena')}</span>
          <span className="block text-o-sub leading-[18px] text-oficina-texto2">{t('oficinaV2.despliegues.filaContrasenaExplica')}</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
      </button>
      <Ventana abierta={abierta} alCerrar={() => setAbierta(false)} titulo={t('oficinaV2.despliegues.fijarTitulo')}>
        <div className="pb-2">
          <FijarContrasena suelta alTerminar={() => setAbierta(false)} />
        </div>
      </Ventana>
    </section>
  );
}
